import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ServiceCategoryTooltip from "./ServiceCategoryTooltip";
import { resolveRfiBadgeStyle } from "../utils/serviceCategoriesColors";
import {
  DEFAULT_RFI_BADGE_STYLE,
  getRfiCapabilityBadgeDisplayName,
} from "../utils/rfiCatalogServiceNames";
import { replaceRfiTerminologyForDisplay } from "../utils/apiResponseRfiToVqqDisplay";
import {
  ADDITIONAL_SCOPE_CHIP_CLASS,
  ADDITIONAL_SCOPE_DIVIDER_CLASS,
} from "../utils/scopeDisplayUtils";
import { compareFoundationFirst } from "../utils/serviceCategoryBadgeDisplay";

const Capabilities = ({
  services,
  icon,
  isExpanded,
  setShowChevron,
  showLimited,
  name,
  showTooltips = true,
  applyRfiCatalogCustomLabels = false,
  stripNewSuffixFromDisplay = false,
  fallbackBadgeStyle = DEFAULT_RFI_BADGE_STYLE,
  badgesPerRow,
  additionalScopeTags,
}) => {
  const { rfis } = useSelector((state) => state.rfisColors);
  const [badgeStyles, setBadgeStyles] = useState([]);
  const normalizedServices = Array.isArray(services) ? services : [];
  const servicesCopy = [...normalizedServices];
  const [isMultiLine, setIsMultiLine] = useState(false);
  const serviceRefs = useRef([]);

  const getServiceLabel = (item) => {
    if (typeof item === "string") {
      return item;
    }
    return item?.rfi_name ?? "";
  };

  const sortedServices = [...servicesCopy].sort((a, b) => {
    const nameA = getServiceLabel(a);
    const nameB = getServiceLabel(b);
    const foundation = compareFoundationFirst(nameA, nameB);
    if (foundation !== 0) return foundation;
    return nameA.length - nameB.length;
  });

  const resolveBadgeStyle = (rfi) =>
    resolveRfiBadgeStyle(rfi, rfis) || fallbackBadgeStyle;

  const mapRfisColors = () => {
    if (sortedServices.length > 0) {
      const styles = sortedServices.map((rfi) => resolveBadgeStyle(rfi));
      setBadgeStyles(styles);
    }
  };

  useEffect(() => {
    mapRfisColors();
  }, [services, rfis]);

  useEffect(() => {
    if (serviceRefs.current.length > 0) {
      const isAnyMultiLine = serviceRefs.current.some((ref) => {
        if (!ref) return false;
        const lineHeight = parseFloat(getComputedStyle(ref).lineHeight);
        return ref.scrollHeight > lineHeight * 1.5;
      });
      setIsMultiLine(isAnyMultiLine);
    }
  }, [services]);

  const maxVisibleTags = name?.length >= 30 ? 1 : 3;
  const scopeTags = Array.isArray(additionalScopeTags)
    ? additionalScopeTags.filter(Boolean)
    : [];
  const totalTagCount = (sortedServices?.length || 0) + scopeTags.length;
  const visibleTagBudget =
    showLimited && !isExpanded
      ? Math.min(maxVisibleTags, totalTagCount)
      : totalTagCount;
  const visibleServiceCount = Math.min(
    sortedServices?.length || 0,
    visibleTagBudget
  );
  const remainingBudget = Math.max(0, visibleTagBudget - visibleServiceCount);
  const visibleScopeTags =
    showLimited && !isExpanded
      ? scopeTags.slice(0, remainingBudget)
      : scopeTags;
  const hiddenTagCount = totalTagCount - visibleServiceCount - visibleScopeTags.length;
  const remainingServices = sortedServices?.slice(visibleServiceCount);

  useEffect(() => {
    if (showLimited) {
      setShowChevron(hiddenTagCount > 0);
    }
  }, [showLimited, hiddenTagCount]);

  const renderBadge = (item, index) => {
    const rawLabel =
      (typeof item === "string" ? item : undefined) ||
      item?.rfi_name ||
      (typeof item?.services === "string" ? item.services : undefined) ||
      item?.geographical_region ||
      item?.service_compliance_type ||
      item?.indication ||
      item?.therapeutic_area ||
      item?.diversity_classification;

    const badgeBase = applyRfiCatalogCustomLabels
      ? getRfiCapabilityBadgeDisplayName(rawLabel)
      : rawLabel;
    const displayBadgeBase =
      stripNewSuffixFromDisplay && typeof badgeBase === "string"
        ? badgeBase.replace(/New$/i, "").trim()
        : badgeBase;
    const displayName =
      typeof displayBadgeBase === "string"
        ? replaceRfiTerminologyForDisplay(displayBadgeBase)
        : displayBadgeBase;

    const styles = badgeStyles[index] || {};

    return (
      <div key={index} ref={(el) => (serviceRefs.current[index] = el)}>
        <span
          style={{
            backgroundColor: styles.bgColor,
            color: styles.textColor,
            boxShadow: `inset 0 0 0 1px ${styles.borderColor}1A`, // Simulates ring-*/* effect (10% opacity)
          }}
          className="inline-flex items-center rounded px-2 text-xs font-medium py-1 space-x-2"
        >
          <span>{displayName}</span>
          {(showTooltips || icon) && (
            <ServiceCategoryTooltip name={displayName || rawLabel} />
          )}
        </span>
      </div>
    );
  };

  const renderAdditionalScopeTags = () => {
    if (!visibleScopeTags.length) return null;
    return (
      <>
        {(visibleServiceCount > 0 && visibleScopeTags.length > 0) && (
          <div className={ADDITIONAL_SCOPE_DIVIDER_CLASS} />
        )}
        {visibleScopeTags.map((tag, index) => (
          <span key={`scope-tag-${index}`} className={ADDITIONAL_SCOPE_CHIP_CLASS}>
            {tag}
          </span>
        ))}
      </>
    );
  };

  const visibleEntries =
    sortedServices?.reduce((acc, item, index) => {
      if (index >= visibleServiceCount) return acc;
      acc.push({ item, index });
      return acc;
    }, []) ?? [];

  const useRowGroups =
    typeof badgesPerRow === "number" && badgesPerRow > 0 && visibleEntries.length > 0;

  if (useRowGroups) {
    const rows = [];
    for (let i = 0; i < visibleEntries.length; i += badgesPerRow) {
      rows.push(visibleEntries.slice(i, i + badgesPerRow));
    }
    return (
      <div className="flex flex-col gap-y-1.5">
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="flex flex-wrap items-center gap-x-1 gap-y-1"
          >
            {row.map(({ item, index }) => renderBadge(item, index))}
            {rowIndex === rows.length - 1 ? renderAdditionalScopeTags() : null}
          </div>
        ))}
        {showLimited && hiddenTagCount > 0 && (
          <span className="inline-flex w-fit items-center rounded px-2 text-xs font-medium py-1 border space-x-2 bg-gray-300 text-gray-600">
            {` +${hiddenTagCount} more`}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {visibleEntries.map(({ item, index }) => renderBadge(item, index))}
      {renderAdditionalScopeTags()}
      {showLimited && hiddenTagCount > 0 && (
        <span className="flex items-center rounded px-2 text-xs font-medium py-1 border space-x-2 bg-gray-300 text-gray-600">
          {` +${hiddenTagCount} more`}
        </span>
      )}
    </div>
  );
};

export default Capabilities;
